import React, { useState, useEffect } from "react";
import axios from 'axios';

const PublicListingsPage = ({ onBack }) => {
  const [listings, setListings] = useState([]);
  const [filteredListings, setFilteredListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedListing, setSelectedListing] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('newest');
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    prefecture: '',
    municipality: '',
    priceRange: '',
    propertyType: '',
    roomType: '',
    featured: false
  });

  const itemsPerPage = 12;

  useEffect(() => {
    loadPublicListings();
  }, []);

  useEffect(() => {
    applyFiltersAndSearch();
  }, [listings, searchTerm, filters, sortBy]);

  const loadPublicListings = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch approved listings from the public API
      const response = await axios.get('https://app.akiya2.com/approved_listings');
      
      // Handle different response structures
      let listingsData = [];
      if (response.data) {
        if (Array.isArray(response.data)) {
          listingsData = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          listingsData = response.data.data;
        } else if (response.data.listings && Array.isArray(response.data.listings)) {
          listingsData = response.data.listings;
        }
      }
      
      // Filter only listings that have agentId (added by agents)
      const agentListings = listingsData.filter(listing => 
        listing.agentId && 
        listing.status === 'active' && 
        listing.agentId !== 'admin'
      );
      
      setListings(agentListings);
      console.log(`Loaded ${agentListings.length} approved agent listings`);
      
    } catch (error) {
      console.error('Failed to load listings:', error);
      setError('Failed to load property listings. Please try again.');
      setListings([]);
    } finally {
      setLoading(false);
    }
  };

  const loadListingsByMunicipality = async (municipality) => {
    try {
      setLoading(true);
      const response = await axios.get(`https://app.akiya2.com/public/approved-listings/${municipality}`);
      
      let listingsData = [];
      if (response.data) {
        if (Array.isArray(response.data)) {
          listingsData = response.data;
        } else if (response.data.data) {
          listingsData = response.data.data;
        }
      }
      
      const agentListings = listingsData.filter(listing => 
        listing.agentId && 
        listing.status === 'active' && 
        listing.agentId !== 'admin'
      );
      
      setListings(agentListings);
    } catch (error) {
      console.error('Failed to load listings by municipality:', error);
      setError(`Failed to load listings for ${municipality}. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  const applyFiltersAndSearch = () => {
    let filtered = [...listings];

    // Apply search
    if (searchTerm) {
      filtered = filtered.filter(listing =>
        listing.Address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        listing.Prefecture?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        listing.Municipality?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        listing.municipality?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        listing.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply filters
    if (filters.prefecture) {
      filtered = filtered.filter(listing => 
        listing.Prefecture?.toLowerCase() === filters.prefecture.toLowerCase()
      );
    }

    if (filters.municipality) {
      filtered = filtered.filter(listing => 
        (listing.Municipality?.toLowerCase() === filters.municipality.toLowerCase()) ||
        (listing.municipality?.toLowerCase() === filters.municipality.toLowerCase())
      );
    }

    if (filters.priceRange) {
      filtered = filtered.filter(listing => {
        const price = parseInt(listing.Price?.replace(/[^\d]/g, '') || '0');
        switch (filters.priceRange) {
          case 'under-5m': return price < 5000000;
          case '5m-10m': return price >= 5000000 && price < 10000000;
          case '10m-20m': return price >= 10000000 && price < 20000000;
          case 'over-20m': return price >= 20000000;
          default: return true;
        }
      });
    }

    if (filters.propertyType) {
      filtered = filtered.filter(listing => 
        listing.Empty_or_Not?.toLowerCase() === filters.propertyType.toLowerCase()
      );
    }

    if (filters.roomType) {
      filtered = filtered.filter(listing => 
        listing.Room_Type?.toLowerCase() === filters.roomType.toLowerCase()
      );
    }

    if (filters.featured) {
      filtered = filtered.filter(listing => listing.featured === true);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'price-low':
          return (parseInt(a.Price?.replace(/[^\d]/g, '') || '0')) - (parseInt(b.Price?.replace(/[^\d]/g, '') || '0'));
        case 'price-high':
          return (parseInt(b.Price?.replace(/[^\d]/g, '') || '0')) - (parseInt(a.Price?.replace(/[^\d]/g, '') || '0'));
        case 'year-new':
          return (parseInt(b.Year_of_Construction || '0')) - (parseInt(a.Year_of_Construction || '0'));
        case 'year-old':
          return (parseInt(a.Year_of_Construction || '0')) - (parseInt(b.Year_of_Construction || '0'));
        case 'featured':
          return (b.featured ? 1 : 0) - (a.featured ? 1 : 0);
        case 'newest':
        default:
          return new Date(b.createdAt || b.created_at || 0) - new Date(a.createdAt || a.created_at || 0);
      }
    });

    setFilteredListings(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  };

  const formatPrice = (price) => {
    if (!price) return 'Contact for Price';
    const numericPrice = price.toString().replace(/[^\d]/g, '');
    return `¥${parseInt(numericPrice).toLocaleString()}`;
  };

  const getUniqueValues = (field) => {
    const values = listings.map(listing => {
      // Handle both Municipality and municipality fields
      if (field === 'Municipality') {
        return listing.Municipality || listing.municipality;
      }
      return listing[field];
    }).filter(Boolean);
    return [...new Set(values)].sort();
  };

  const handleViewDetails = (listing) => {
    setSelectedListing(listing);
    setShowDetailModal(true);
  };

  const clearFilters = () => {
    setFilters({
      prefecture: '',
      municipality: '',
      priceRange: '',
      propertyType: '',
      roomType: '',
      featured: false
    });
    setSearchTerm('');
    setSortBy('newest');
  };

  const handleRefresh = () => {
    setError(null);
    loadPublicListings();
  };

  // Pagination
  const totalPages = Math.ceil(filteredListings.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentListings = filteredListings.slice(startIndex, endIndex);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#fafafa] flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-16 h-16 bg-gradient-to-br from-[#10c9c0] to-[#0eb1a9] rounded-2xl flex items-center justify-center animate-pulse">
            <i className="fas fa-home text-white text-2xl"></i>
          </div>
          <p className="text-gray-600">Loading properties...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fafafa]" style={{ fontFamily: '"Libre Franklin", sans-serif' }}>
      {/* FontAwesome CDN */}
      <link 
        rel="stylesheet" 
        href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css" 
        integrity="sha512-iecdLmaskl7CVkqkXNQ/ZH/XLlvWZOJyj7Yy7tcenmpD1ypASozpmT/E0iPtmFIB46ZmdtAc9eNBvH0H/ZpiBw==" 
        crossOrigin="anonymous" 
        referrerPolicy="no-referrer" 
      />

      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
            <div className="flex items-center space-x-4">
              {onBack && (
                <button 
                  onClick={onBack}
                  className="w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-xl flex items-center justify-center transition-colors"
                >
                  <i className="fas fa-arrow-left text-gray-600"></i>
                </button>
              )}
              <div>
                <h1 className="text-3xl font-bold text-gray-800 mb-1">Agent Property Listings</h1>
                <p className="text-gray-600">
                  {filteredListings.length} properties available from verified agents
                  {searchTerm && ` for "${searchTerm}"`}
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <button
                onClick={handleRefresh}
                className="px-4 py-2 bg-blue-100 text-blue-700 rounded-xl hover:bg-blue-200 transition-colors"
                disabled={loading}
              >
                <i className="fas fa-sync-alt mr-2"></i>
                {loading ? 'Loading...' : 'Refresh'}
              </button>
              
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search by location, address..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-3 w-80 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10c9c0] focus:border-[#10c9c0] transition-all"
                />
                <i className="fas fa-search absolute left-3 top-4 text-gray-400"></i>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Error Display */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 pt-4">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <i className="fas fa-exclamation-triangle mr-2"></i>
                <span>{error}</span>
              </div>
              <div className="flex items-center space-x-2">
                <button 
                  onClick={handleRefresh}
                  className="px-3 py-1 bg-red-200 text-red-800 rounded-lg hover:bg-red-300 transition-colors text-sm"
                >
                  Retry
                </button>
                <button 
                  onClick={() => setError(null)}
                  className="hover:opacity-70 transition-opacity"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters and Controls */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
            {/* Filters */}
            <div className="flex flex-wrap gap-3">
              <select 
                value={filters.prefecture}
                onChange={(e) => setFilters({...filters, prefecture: e.target.value})}
                className="px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10c9c0] focus:border-[#10c9c0] transition-all"
              >
                <option value="">All Prefectures</option>
                {getUniqueValues('Prefecture').map(prefecture => (
                  <option key={prefecture} value={prefecture}>{prefecture}</option>
                ))}
              </select>

              <select 
                value={filters.municipality}
                onChange={(e) => {
                  setFilters({...filters, municipality: e.target.value});
                  // Optionally load specific municipality data
                  if (e.target.value) {
                    loadListingsByMunicipality(e.target.value);
                  } else {
                    loadPublicListings();
                  }
                }}
                className="px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10c9c0] focus:border-[#10c9c0] transition-all"
              >
                <option value="">All Municipalities</option>
                {getUniqueValues('Municipality').map(municipality => (
                  <option key={municipality} value={municipality}>{municipality}</option>
                ))}
              </select>

              <select 
                value={filters.priceRange}
                onChange={(e) => setFilters({...filters, priceRange: e.target.value})}
                className="px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10c9c0] focus:border-[#10c9c0] transition-all"
              >
                <option value="">All Prices</option>
                <option value="under-5m">Under ¥5M</option>
                <option value="5m-10m">¥5M - ¥10M</option>
                <option value="10m-20m">¥10M - ¥20M</option>
                <option value="over-20m">Over ¥20M</option>
              </select>

              <select 
                value={filters.propertyType}
                onChange={(e) => setFilters({...filters, propertyType: e.target.value})}
                className="px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10c9c0] focus:border-[#10c9c0] transition-all"
              >
                <option value="">All Types</option>
                <option value="Sale">For Sale</option>
                <option value="Rental">For Rental</option>
              </select>

              <select 
                value={filters.roomType}
                onChange={(e) => setFilters({...filters, roomType: e.target.value})}
                className="px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10c9c0] focus:border-[#10c9c0] transition-all"
              >
                <option value="">All Room Types</option>
                {getUniqueValues('Room_Type').map(roomType => (
                  <option key={roomType} value={roomType}>{roomType}</option>
                ))}
              </select>

              <label className="flex items-center px-4 py-2 border border-gray-200 rounded-xl cursor-pointer hover:bg-gray-50 transition-colors">
                <input
                  type="checkbox"
                  checked={filters.featured}
                  onChange={(e) => setFilters({...filters, featured: e.target.checked})}
                  className="mr-2 text-[#10c9c0] rounded"
                />
                <i className="fas fa-star mr-1 text-gray-400"></i>
                Featured Only
              </label>

              {(filters.prefecture || filters.municipality || filters.priceRange || filters.propertyType || filters.roomType || filters.featured) && (
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  <i className="fas fa-times mr-1"></i>
                  Clear Filters
                </button>
              )}
            </div>

            {/* Sort and View Controls */}
            <div className="flex items-center space-x-3">
              <select 
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10c9c0] focus:border-[#10c9c0] transition-all"
              >
                <option value="newest">Newest First</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="year-new">Year: Newest</option>
                <option value="year-old">Year: Oldest</option>
                <option value="featured">Featured First</option>
              </select>

              <div className="flex bg-gray-100 rounded-xl p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`px-3 py-2 rounded-lg transition-all ${
                    viewMode === 'grid' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'
                  }`}
                >
                  <i className="fas fa-th-large"></i>
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`px-3 py-2 rounded-lg transition-all ${
                    viewMode === 'list' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'
                  }`}
                >
                  <i className="fas fa-list"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {filteredListings.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12">
            <div className="text-center">
              <i className="fas fa-search text-gray-400 text-6xl mb-6"></i>
              <h3 className="text-xl font-bold text-gray-800 mb-2">No properties found</h3>
              <p className="text-gray-600 mb-6">
                {searchTerm || Object.values(filters).some(f => f) 
                  ? 'Try adjusting your search criteria or filters' 
                  : 'No properties are currently available from agents'
                }
              </p>
              {(searchTerm || Object.values(filters).some(f => f)) && (
                <button 
                  onClick={clearFilters}
                  className="px-6 py-3 bg-[#10c9c0] text-white rounded-xl hover:bg-[#0eb1a9] transition-colors"
                >
                  <i className="fas fa-times mr-2"></i>
                  Clear All Filters
                </button>
              )}
            </div>
          </div>
        ) : (
          <>
            {/* Listings Display */}
            <div className={viewMode === 'grid' 
              ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" 
              : "space-y-6"
            }>
              {currentListings.map((listing) => (
                viewMode === 'grid' ? (
                  <PropertyCard 
                    key={listing.property_id} 
                    listing={listing} 
                    onViewDetails={handleViewDetails}
                    formatPrice={formatPrice}
                  />
                ) : (
                  <PropertyRow 
                    key={listing.property_id} 
                    listing={listing} 
                    onViewDetails={handleViewDetails}
                    formatPrice={formatPrice}
                  />
                )
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center space-x-2 mt-12">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className={`px-4 py-2 rounded-xl transition-all ${
                    currentPage === 1
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <i className="fas fa-chevron-left"></i>
                </button>

                {[...Array(totalPages)].map((_, index) => {
                  const page = index + 1;
                  if (
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPage - 2 && page <= currentPage + 2)
                  ) {
                    return (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`px-4 py-2 rounded-xl transition-all ${
                          currentPage === page
                            ? 'bg-[#10c9c0] text-white'
                            : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  } else if (
                    page === currentPage - 3 ||
                    page === currentPage + 3
                  ) {
                    return <span key={page} className="px-2 text-gray-400">...</span>;
                  }
                  return null;
                })}

                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className={`px-4 py-2 rounded-xl transition-all ${
                    currentPage === totalPages
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <i className="fas fa-chevron-right"></i>
                </button>
              </div>
            )}
          </>
        )}
      </main>

      {/* Property Detail Modal */}
      {showDetailModal && selectedListing && (
        <PropertyDetailModal
          listing={selectedListing}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedListing(null);
          }}
          formatPrice={formatPrice}
        />
      )}
    </div>
  );
};

// Property Card Component (Grid View)
const PropertyCard = ({ listing, onViewDetails, formatPrice }) => (
  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 cursor-pointer">
    {/* Image Section */}
    <div className="h-48 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center relative overflow-hidden">
      {listing.Images && listing.Images.length > 0 ? (
        <img 
          src={listing.Images[0]} 
          alt={listing.Address}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'flex';
          }}
        />
      ) : null}
      <div className="w-full h-full flex items-center justify-center">
        <i className="fas fa-building text-gray-400 text-4xl"></i>
      </div>
      
      {/* Featured Badge */}
      {listing.featured && (
        <div className="absolute top-4 right-4 bg-gradient-to-r from-[#10c9c0] to-[#0eb1a9] text-white px-3 py-1 rounded-full text-sm font-medium">
          <i className="fas fa-star mr-1"></i>
          Featured
        </div>
      )}

      {/* Property Type Badge */}
      <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm text-gray-800 px-3 py-1 rounded-full text-sm font-medium">
        {listing.Empty_or_Not}
      </div>

      {/* Agent Badge */}
      <div className="absolute bottom-4 right-4 bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">
        Agent: {listing.agentId}
      </div>
    </div>
    
    <div className="p-6" onClick={() => onViewDetails(listing)}>
      <div className="mb-4">
        <h3 className="text-lg font-bold text-gray-800 mb-2 line-clamp-2">{listing.Address}</h3>
        <p className="text-gray-600 text-sm">{listing.Prefecture} • {listing.Municipality || listing.municipality}</p>
      </div>
      
      <div className="space-y-3 mb-4">
        <div className="flex items-center text-[#10c9c0]">
          <i className="fas fa-yen-sign w-5 mr-2"></i>
          <span className="font-bold text-xl">{formatPrice(listing.Price)}</span>
        </div>
        
        <div className="flex items-center space-x-4 text-gray-600 text-sm">
          <div className="flex items-center">
            <i className="fas fa-calendar mr-1"></i>
            <span>{listing.Year_of_Construction}</span>
          </div>
          {listing.Room_Type && (
            <div className="flex items-center">
              <i className="fas fa-door-open mr-1"></i>
              <span>{listing.Room_Type}</span>
            </div>
          )}
          {listing.Area_of_House && (
            <div className="flex items-center">
              <i className="fas fa-ruler-combined mr-1"></i>
              <span>{listing.Area_of_House}m²</span>
            </div>
          )}
        </div>
      </div>
      
      <button 
        onClick={(e) => {
          e.stopPropagation();
          onViewDetails(listing);
        }}
        className="w-full bg-[#10c9c0] text-white py-3 rounded-xl hover:bg-[#0eb1a9] transition-colors font-medium"
      >
        View Details
      </button>
    </div>
  </div>
);

// Property Row Component (List View)
const PropertyRow = ({ listing, onViewDetails, formatPrice }) => (
  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-all cursor-pointer" onClick={() => onViewDetails(listing)}>
    <div className="flex items-center space-x-6">
      <div className="w-32 h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl flex items-center justify-center flex-shrink-0">
        {listing.Images && listing.Images.length > 0 ? (
          <img 
            src={listing.Images[0]} 
            alt={listing.Address}
            className="w-full h-full object-cover rounded-xl"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
        ) : null}
        <i className="fas fa-building text-gray-400 text-2xl"></i>
      </div>
      
      <div className="flex-1">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-bold text-gray-800 mb-1">{listing.Address}</h3>
            <p className="text-gray-600 text-sm mb-2">{listing.Prefecture} • {listing.Municipality || listing.municipality}</p>
            <div className="flex items-center space-x-4 text-sm text-gray-600">
              <span>{listing.Year_of_Construction}</span>
              <span>{listing.Empty_or_Not}</span>
              {listing.Room_Type && <span>{listing.Room_Type}</span>}
              {listing.Area_of_House && <span>{listing.Area_of_House}m²</span>}
              <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                Agent: {listing.agentId}
              </span>
            </div>
          </div>
          
          <div className="text-right">
            <div className="text-2xl font-bold text-[#10c9c0] mb-2">{formatPrice(listing.Price)}</div>
            {listing.featured && (
              <div className="inline-flex items-center px-3 py-1 bg-[#10c9c0]/10 text-[#0eb1a9] rounded-full text-sm font-medium">
                <i className="fas fa-star mr-1"></i>
                Featured
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Property Detail Modal Component
const PropertyDetailModal = ({ listing, onClose, formatPrice }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
    <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
      <div className="relative">
        {/* Header Image */}
        <div className="h-64 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center relative">
          {listing.Images && listing.Images.length > 0 ? (
            <img 
              src={listing.Images[0]} 
              alt={listing.Address}
              className="w-full h-full object-cover"
            />
          ) : (
            <i className="fas fa-building text-gray-400 text-6xl"></i>
          )}
          
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 w-10 h-10 bg-white/90 backdrop-blur-sm hover:bg-white rounded-xl flex items-center justify-center transition-colors"
          >
            <i className="fas fa-times text-gray-600"></i>
          </button>

          {listing.featured && (
            <div className="absolute top-4 left-4 bg-gradient-to-r from-[#10c9c0] to-[#0eb1a9] text-white px-4 py-2 rounded-full font-medium">
              <i className="fas fa-star mr-2"></i>
              Featured Property
            </div>
          )}

          {/* Agent Info Badge */}
          <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm text-gray-800 px-4 py-2 rounded-full font-medium">
            <i className="fas fa-user-tie mr-2"></i>
            Agent: {listing.agentId}
          </div>
        </div>

        <div className="p-8">
          {/* Property Info */}
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">{listing.Address}</h2>
            <div className="flex items-center space-x-4 text-gray-600 mb-4">
              <span><i className="fas fa-map-marker-alt mr-2"></i>{listing.Prefecture}</span>
              <span>•</span>
              <span>{listing.Municipality || listing.municipality}</span>
            </div>
            <div className="text-3xl font-bold text-[#10c9c0] mb-6">{formatPrice(listing.Price)}</div>
          </div>

          {/* Property Details Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Property Details</h3>
              
              {[
                { label: 'Property Type', value: listing.Empty_or_Not },
                { label: 'Year Built', value: listing.Year_of_Construction },
                { label: 'Room Type', value: listing.Room_Type },
                { label: 'Number of Floors', value: listing.No_of_Floors },
                { label: 'House Area', value: listing.Area_of_House ? `${listing.Area_of_House}m²` : null },
                { label: 'Land Area', value: listing.Area_of_Land ? `${listing.Area_of_Land}m²` : null },
                { label: 'Property ID', value: listing.property_id }
              ].filter(item => item.value).map((item, index) => (
                <div key={index} className="flex justify-between py-2 border-b border-gray-100">
                  <span className="font-medium text-gray-600">{item.label}:</span>
                  <span className="text-gray-800">{item.value}</span>
                </div>
              ))}
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Contact Information</h3>
              
              <div className="p-4 bg-gray-50 rounded-xl">
                <p className="text-gray-800 mb-2">
                  <i className="fas fa-phone mr-2 text-[#10c9c0]"></i>
                  {listing.Contact_Info}
                </p>
                <p className="text-gray-600 text-sm mb-2">
                  <i className="fas fa-user-tie mr-2"></i>
                  Agent ID: {listing.agentId}
                </p>
                {listing.agent_name && (
                  <p className="text-gray-600 text-sm">
                    <i className="fas fa-user mr-2"></i>
                    {listing.agent_name}
                  </p>
                )}
                {listing.createdAt && (
                  <p className="text-gray-500 text-xs mt-2">
                    <i className="fas fa-calendar mr-2"></i>
                    Listed: {new Date(listing.createdAt).toLocaleDateString()}
                  </p>
                )}
              </div>

              {listing.Link_to_Website && (
                <a 
                  href={listing.Link_to_Website.startsWith('http') ? listing.Link_to_Website : `https://${listing.Link_to_Website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 bg-[#10c9c0] text-white rounded-xl hover:bg-[#0eb1a9] transition-colors"
                >
                  <i className="fas fa-external-link-alt mr-2"></i>
                  View Website
                </a>
              )}
            </div>
          </div>

          {/* Description */}
          {listing.description && (
            <div className="mb-8">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Description</h3>
              <div className="p-4 bg-gray-50 rounded-xl">
                <p className="text-gray-800 leading-relaxed">{listing.description}</p>
              </div>
            </div>
          )}

          {/* Image Gallery */}
          {listing.Images && listing.Images.length > 1 && (
            <div className="mb-8">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Property Images ({listing.Images.length})</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {listing.Images.slice(1).map((image, index) => (
                  <div key={index} className="aspect-square bg-gray-100 rounded-xl overflow-hidden">
                    <img 
                      src={image} 
                      alt={`${listing.Address} - Image ${index + 2}`}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300 cursor-pointer"
                      onClick={() => window.open(image, '_blank')}
                      onError={(e) => {
                        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtc2l6ZT0iMTQiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBOb3QgRm91bmQ8L3RleHQ+PC9zdmc+';
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Supporting Documents */}
          {(listing.supportingDocType && listing.supportingDocContent) && (
            <div className="mb-8">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Supporting Documents</h3>
              <div className="p-4 bg-gray-50 rounded-xl">
                {listing.supportingDocType === 'link' ? (
                  <a 
                    href={listing.supportingDocContent.startsWith('http') ? listing.supportingDocContent : `https://${listing.supportingDocContent}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-blue-600 hover:text-blue-800 underline"
                  >
                    <i className="fas fa-external-link-alt mr-2"></i>
                    {listing.supportingDocContent}
                  </a>
                ) : (
                  <div className="flex items-center">
                    <i className="fas fa-file-pdf text-gray-600 text-2xl mr-3"></i>
                    <div>
                      <p className="font-medium text-gray-800">Document Available</p>
                      <p className="text-sm text-gray-600">{listing.supportingDocContent}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Map placeholder */}
          {listing.Coordinates && listing.Coordinates.length === 2 && (
            <div className="mb-8">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Location</h3>
              <div className="h-64 bg-gray-100 rounded-xl flex items-center justify-center">
                <div className="text-center">
                  <i className="fas fa-map-marker-alt text-gray-400 text-4xl mb-4"></i>
                  <p className="text-gray-600">
                    Coordinates: {listing.Coordinates[0]}, {listing.Coordinates[1]}
                  </p>
                  <p className="text-sm text-gray-500 mt-2">
                    Interactive map integration available
                  </p>
                  <a 
                    href={`https://www.google.com/maps?q=${listing.Coordinates[0]},${listing.Coordinates[1]}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center mt-3 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm"
                  >
                    <i className="fas fa-map mr-2"></i>
                    View on Google Maps
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* Agent Information */}
          <div className="mb-8">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Listed By Agent</h3>
            <div className="p-4 bg-blue-50 rounded-xl">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center">
                  <i className="fas fa-user-tie text-white"></i>
                </div>
                <div>
                  <p className="font-medium text-gray-800">Agent ID: {listing.agentId}</p>
                  <p className="text-sm text-gray-600">Verified Property Agent</p>
                  {listing.updatedAt && (
                    <p className="text-xs text-gray-500">
                      Last updated: {new Date(listing.updatedAt).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4">
            <button 
              onClick={() => {
                if (navigator.share) {
                  navigator.share({
                    title: listing.Address,
                    text: `Check out this property: ${listing.Address}`,
                    url: window.location.href
                  });
                } else {
                  navigator.clipboard.writeText(window.location.href);
                  alert('Link copied to clipboard!');
                }
              }}
              className="flex-1 bg-gray-200 text-gray-700 py-4 rounded-xl hover:bg-gray-300 transition-colors font-medium"
            >
              <i className="fas fa-share mr-2"></i>
              Share Property
            </button>
            
            <button 
              onClick={() => {
                const phone = listing.Contact_Info.replace(/[^\d+]/g, '');
                window.open(`tel:${phone}`, '_self');
              }}
              className="flex-1 bg-[#10c9c0] text-white py-4 rounded-xl hover:bg-[#0eb1a9] transition-colors font-medium"
            >
              <i className="fas fa-phone mr-2"></i>
              Call Agent
            </button>
            
            <button 
              onClick={() => {
                const message = `Hi, I'm interested in your property listing: ${listing.Address}. Could you provide more information?`;
                const phone = listing.Contact_Info.replace(/[^\d+]/g, '');
                window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, '_blank');
              }}
              className="flex-1 bg-green-500 text-white py-4 rounded-xl hover:bg-green-600 transition-colors font-medium"
            >
              <i className="fab fa-whatsapp mr-2"></i>
              WhatsApp
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default PublicListingsPage;